import { useEffect, useRef } from "react";
import { GlobalEntityState, globalStateApply, globalStateIsEqual } from "../utils/state/index.ts";
import { SceneDataConfig } from "../../config/types.ts";
import { SceneAudioConfig } from "../../config/audio/types.ts";
import { resolveBackground } from "../loaders.ts";
import { LOGS } from "../utils/logging.ts";

interface UseStateSyncProps {
	GlobalEntityState: GlobalEntityState;
	originalSceneConfig: SceneDataConfig;
	sceneName?: string;
	onVideoDataReady: (data: {
		background: {
			data: ArrayBuffer;
			contentType: string;
			src: string;
			navigateAfter?: string;
		};
	}) => void;
	onAudioDataReady?: (audio: SceneAudioConfig) => void;
}

/**
 * Hook to handle video reloading when scene state changes
 */
export function useStateSync({
	GlobalEntityState,
	originalSceneConfig,
	sceneName,
	onVideoDataReady,
	onAudioDataReady,
}: UseStateSyncProps) {
	const currentSceneRef = useRef<string | undefined>(sceneName);
	const previousStateRef = useRef<GlobalEntityState>(GlobalEntityState);
	const loadingRef = useRef<boolean>(false);
	const newVideo = async (sceneConfig: SceneDataConfig) => {
		LOGS.VIDEO && console.log("reloading video...");
		try {
			const backgroundData = await resolveBackground(sceneConfig.background.src);

			if (!backgroundData || !backgroundData.contentType || !backgroundData.data) {
				console.error("Failed to load background data for new state");
				return false;
			}

			const { contentType, data } = backgroundData;

			onVideoDataReady({
				background: {
					data,
					contentType,
					src: sceneConfig.background.src,
					navigateAfter: sceneConfig.navigateAfterVideo ?? undefined,
				},
			});

			return false;
		} catch (err) {
			console.error("Error loading new video for state change:", err);
			return false;
		}
	};
	useEffect(() => {
		const previousState = previousStateRef.current;
		const stateChanged = !globalStateIsEqual(previousState, GlobalEntityState);

		const isSceneChange = currentSceneRef.current !== sceneName;
		if (isSceneChange) {
			currentSceneRef.current = sceneName;
		}

		const needsReload = stateChanged || isSceneChange;

		if (needsReload) {
			loadingRef.current = true;
			const updatedSceneConfig = globalStateApply(originalSceneConfig, GlobalEntityState);
			previousStateRef.current = GlobalEntityState;

			if (onAudioDataReady) {
				onAudioDataReady(updatedSceneConfig.audio);
			}

			newVideo(updatedSceneConfig).then(() => (loadingRef.current = false));
		}
	}, [GlobalEntityState, originalSceneConfig, onVideoDataReady, onAudioDataReady, sceneName]);

	return {
		isLoading: loadingRef.current,
	};
}
